import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Tab, Tabs } from "@mui/material";
import Toast from "../../../components/MessageToast";
import DeleteConfirm from "../../../components/Dialogs/DeleteConfirm";
import ConfirmationDialog from "../../../components/Dialogs/ConfirmationDialog";
import MetricsList from "../../components/metric/MetricsList";
import CreateMetric from "./CreateMetric";
import { fetchMetricsData } from "../../../redux/metrics/index";
import actionCreator from "../../../redux/action/actionCreator";
import { DELETE_METRIC } from "../../../redux/metrics/types";
import {
  PUBLISH_OR_UNPUBLISH_CONTENT,
  RESET_PUBLISH_STORE,
} from "../../../redux/general/types";
import {
  DEFAULT_METRIC_CATEGORY,
  DEFAULT_METRIC_PAGE,
  METRIC_CATEGORY_OPTIONS,
  getMetricCategoryLabel,
} from "../../components/metric/metricOptions";

const INITIAL_DIALOGS_STATE = {
  formOpen: false,
  editingMetric: null,
  deleteTarget: null,
  publishTarget: null,
};

// No detail page, no separate create/edit route - Reach and Impact are each
// a handful of curated stat tiles, not a resource that needs full-page
// navigation per item. Everything (list, add, edit, publish, delete) lives
// on this one page; add/edit opens the form in a dialog, publish/delete open
// a confirmation dialog, and every action just refetches this same list.
export default () => {
  const metricsData = useSelector((state) => state.metricsGlobalStore);
  const deleteStore = useSelector((state) => state.deleteMetricGlobalStore);
  const publishStore = useSelector((state) => state.publishGlobalStore);
  const dispatch = useDispatch();

  const [activeCategory, setActiveCategory] = useState(DEFAULT_METRIC_CATEGORY);
  const [dialogs, setDialogs] = useState(INITIAL_DIALOGS_STATE);
  const [pendingToast, setPendingToast] = useState(null);

  const refetch = () => dispatch(fetchMetricsData(activeCategory, DEFAULT_METRIC_PAGE));

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory]);

  const items = useMemo(() => metricsData.data?.data || [], [metricsData.data]);

  const handleCategoryChange = (event, category) => {
    setActiveCategory(category);
    setDialogs(INITIAL_DIALOGS_STATE);
  };

  const openCreateDialog = () =>
    setDialogs({ ...INITIAL_DIALOGS_STATE, formOpen: true });

  const openEditDialog = (metric) =>
    setDialogs({ ...INITIAL_DIALOGS_STATE, formOpen: true, editingMetric: metric });

  const closeFormDialog = () =>
    setDialogs((prev) => ({ ...prev, formOpen: false, editingMetric: null }));

  const handleSaved = () => {
    closeFormDialog();
    refetch();
  };

  const openDeleteDialog = (metric) =>
    setDialogs((prev) => ({ ...prev, deleteTarget: metric }));

  const closeDeleteDialog = () =>
    setDialogs((prev) => ({ ...prev, deleteTarget: null }));

  const handleDeleteConfirm = async () => {
    const target = dialogs.deleteTarget;
    if (!target) return;

    await dispatch(
      actionCreator({
        method: "DELETE",
        path: `/api/metrics/${target.id}`,
        actionType: DELETE_METRIC,
      })
    );
    setDialogs((prev) => ({ ...prev, deleteTarget: null }));
    setPendingToast("delete");
  };

  const openPublishDialog = (metric) =>
    setDialogs((prev) => ({ ...prev, publishTarget: metric }));

  const closePublishDialog = () =>
    setDialogs((prev) => ({ ...prev, publishTarget: null }));

  const handlePublishConfirm = async () => {
    const target = dialogs.publishTarget;
    if (!target) return;

    const isUnpublish = target.status === "published";
    dispatch(actionCreator({ method: "", path: "", actionType: RESET_PUBLISH_STORE }));
    await dispatch(
      actionCreator({
        method: "PATCH",
        path: `/api/metrics/${target.id}`,
        actionType: PUBLISH_OR_UNPUBLISH_CONTENT,
        data: { status: isUnpublish ? "unpublished" : "published" },
      })
    );
    setDialogs((prev) => ({ ...prev, publishTarget: null }));
    setPendingToast("publish");
  };

  if (pendingToast === "delete" && deleteStore.message) {
    Toast({
      message: deleteStore.message,
      type: deleteStore.message.includes("successful") ? "success" : "error",
      onClose: refetch,
    });
    setPendingToast(null);
  }

  if (pendingToast === "publish" && publishStore.message) {
    Toast({
      message: publishStore.message,
      type: publishStore.message.includes("successful") ? "success" : "error",
      onClose: refetch,
    });
    setPendingToast(null);
  }

  const activeCategoryLabel = getMetricCategoryLabel(activeCategory);
  const isUnpublishTarget = dialogs.publishTarget?.status === "published";

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        <Tabs
          value={activeCategory}
          onChange={handleCategoryChange}
          indicatorColor="secondary"
          textColor="secondary"
          aria-label="metrics category tabs"
        >
          {METRIC_CATEGORY_OPTIONS.map((categoryOption) => (
            <Tab
              key={categoryOption.value}
              value={categoryOption.value}
              label={categoryOption.label}
            />
          ))}
        </Tabs>

        <Button
          variant="outlined"
          onClick={openCreateDialog}
          sx={{
            margin: "15px",
            color: "#E00186",
            border: "1px solid #E00186",
            fontFamily: "Arial",
            "&:hover": {
              color: "#fff",
              backgroundColor: "#E00186",
              border: "1px solid #E00186",
            },
          }}
        >
          Add {activeCategoryLabel} Metric
        </Button>
      </div>

      <MetricsList
        category={activeCategory}
        items={items}
        isLoading={metricsData.loading}
        onEdit={openEditDialog}
        onTogglePublish={openPublishDialog}
        onDelete={openDeleteDialog}
      />

      <CreateMetric
        open={dialogs.formOpen}
        metric={dialogs.editingMetric}
        category={activeCategory}
        defaultDisplayOrder={items.length}
        onClose={closeFormDialog}
        onSaved={handleSaved}
      />

      <DeleteConfirm
        open={Boolean(dialogs.deleteTarget)}
        dialogMessage="Do you want to delete this metric? Kindly note that this action is irreversible"
        onCancel={closeDeleteDialog}
        onConfirm={handleDeleteConfirm}
      />

      <ConfirmationDialog
        open={Boolean(dialogs.publishTarget)}
        title={isUnpublishTarget ? "Unpublish metric" : "Publish metric"}
        message={`Do you want to ${
          isUnpublishTarget ? "unpublish" : "publish"
        } this metric? Kindly note that this action might be irreversible`}
        confirmText={isUnpublishTarget ? "Unpublish" : "Publish"}
        onCancel={closePublishDialog}
        onConfirm={handlePublishConfirm}
      />
    </div>
  );
};
